import React, { FC, useState, useEffect } from 'react';
import { HoroscopeColor, HoroscopeIconType } from '../../Horoscopes/HoroscopesIcon/HoroscopesIcon-Constants';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@moment/hooks/useTheme.ts';
import './HoroscopesTile.scss';

interface HoroscopesTileProps {
  sign: HoroscopeIconType;
  date: string;
  onClick?: () => void;
}

export const HoroscopesTile: FC<HoroscopesTileProps> = ({ sign, date, onClick }) => {
  const [IconComponent, setIconComponent] = useState<React.FC<React.SVGProps<SVGSVGElement>> | null>(null);
  const { t } = useTranslation();
  const { theme } = useTheme();

  useEffect(() => {
    const loadIcon = async () => {
      try {
        const signLower = sign.toLowerCase();
        const module = await import(`@/assets/horoscopes/${signLower}.svg?react`);
        setIconComponent(() => module.default);
      } catch (error) {
        console.error(`Failed to load icon for ${sign}:`, error);
      }
    };

    loadIcon();
  }, [sign]);

  const backgroundColor = HoroscopeColor[sign as keyof typeof HoroscopeColor];
  
  const translatedSign = t(sign.toString());
  const capitalizedSign = translatedSign.charAt(0).toUpperCase() + translatedSign.slice(1);

  // Add class for light theme to show drop shadow
  const className = `horoscopes-tile-v2 ${theme === 'light' ? 'horoscopes-tile-v2--light' : ''}`;

  return (
    <div
      className={className}
      style={{ backgroundColor }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      data-testid="horoscopes-tile-v2"
    >
      <div className="horoscopes-tile-v2__illustration">
        {IconComponent && <IconComponent className="horoscopes-tile-v2__icon" />}
      </div>
      <div className="horoscopes-tile-v2__content">
        <h3 className="horoscopes-tile-v2__sign heading-md">{capitalizedSign}</h3>
        <p className="horoscopes-tile-v2__date body-md">{date}</p>
      </div>
    </div>
  );
};

export default HoroscopesTile;

